import moment from 'moment'
import { fmtHour, fmtMinutes } from '../../../../../components/PDF/pdfKit'
import { categoriseComplaints } from './complaintCategories'

// Plain-English headline findings for the executive summary, worked out
// from the report data. Each is only said when the data supports it, and
// only when the planner kept the figure it describes in its own section.

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const were = (n) => (n === 1 ? 'was' : 'were')

export const keyFindings = (data, { include }) => {
  const out = []
  const c = data.overall.clinical
  const part = include.part
  const showHospital = part('clinical', 'outcomes') || part('nhsImpact', 'headline')

  if (c.total) {
    const busiest = [...data.days].sort((a, b) => b.clinical.total - a.clinical.total)[0]
    if (data.days.length > 1 && busiest?.clinical.total) {
      out.push(
        `${plural(c.total, 'patient presentation')} across ${data.days.length} operational days; the busiest was ${moment(busiest.key).format('dddd D MMMM')} with ${busiest.clinical.total}.`,
      )
    } else {
      out.push(`${plural(c.total, 'patient presentation')} ${were(c.total)} recorded.`)
    }
    if (c.peakHour && part('clinical', 'hourly')) {
      out.push(
        `Demand peaked between ${fmtHour(c.peakHour.hour)} and ${fmtHour((c.peakHour.hour + 1) % 24)} (${plural(c.peakHour.count, 'presentation')} in that hour across the event).`,
      )
    }
    const categories = Object.entries(categoriseComplaints(c.complaints)).sort((a, b) => b[1] - a[1])
    if (categories.length && part('clinical', 'complaints')) {
      out.push(`The most common presenting category was ${categories[0][0].toLowerCase()} (${categories[0][1]}).`)
    }
    if (c.nonConveyanceRate !== null && showHospital) {
      out.push(
        `${c.nonConveyanceRate}% of patients with a recorded outcome were managed without going to hospital; ${plural(c.conveyed, 'patient')} went on to hospital${c.nhsAmbulanceHandovers ? `, ${c.nhsAmbulanceHandovers} via an NHS ambulance` : ''}.`,
      )
    }
    const occurredYes = c.occurred?.Yes || 0
    if (occurredYes && occurredYes !== c.total && part('clinical', 'headline')) {
      out.push(`${occurredYes} of ${c.total} presentations related to an injury or illness that happened at the event.`)
    }
  } else {
    out.push('No patient presentations were recorded against this event.')
  }

  if (data.controlRoom && part('controlRoom', 'headline')) {
    const s = data.controlRoom.summary
    const r = s.intervals.callToScene
    if (s.total) {
      out.push(
        `The control room managed ${plural(s.total, 'incident')}${r.count ? `; the median time from call to first resource on scene was ${fmtMinutes(r.median)}${part('controlRoom', 'intervals') ? ` (90% within ${fmtMinutes(r.p90)})` : ''}` : ''}.`,
      )
    }
  }

  if (part('treatment', 'table') && data.treatment?.locations?.length) {
    const top = data.treatment.locations[0]
    if (top?.patients) {
      out.push(
        `${top.name} saw the most patients (${top.patients}), with up to ${top.peak.count} in at once and a median stay of ${fmtMinutes(top.lengthOfStay.median)}.`,
      )
    }
  }

  if (include.healthSafety) {
    out.push(
      data.overall.healthSafety
        ? `${plural(data.overall.healthSafety, 'health and safety concern')} ${were(data.overall.healthSafety)} raised with the organiser.`
        : 'No health and safety concerns were raised by our crews.',
    )
  }

  const fb = data.feedback.patients
  if (part('patientFeedback', 'counts') && fb.responses) {
    out.push(
      `Patients left ${plural(fb.responses, 'piece')} of feedback: ${plural(fb.compliments, 'compliment')} and ${plural(fb.complaints, 'complaint')}.`,
    )
  }

  return out
}
